import React, { useMemo } from 'react';

import { generatePositions } from '@/components/Layout/components/GenerateSetlist';
import { RegenenerateButton } from '@/components/Layout/components/GenerateSetlist/components/ActionContent/ActionContent';
import {
	SelectOptionsList,
	SelectOptionsListItem,
} from '@/components/SelectOptionsList';
import { BottomActionLayout } from '@/components/shared/BottomActionLayout/BottomActionLayout';
import { AsaphButton } from '@/components/shared/Button';
import { IndexIndicator } from '@/components/shared/IndexIndicator';
import { BiblePassageInput } from '@/components/Layout/components/GenerateSetlist/components/BiblePassageInput/BiblePassageInput';
import { Label } from '@/components/shared/Inputs/Label';
import { Select } from '@/components/shared/Inputs/Select';
import { TeamMilestones } from '@/config/constants';
import {
	useAddSongsToMyTeamMutation,
	useGenerateSetlistMutation,
	useGetSongColletionMutation,
} from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useTeam } from '@/providers/TeamProvider';
import { useTeamSongs } from '@/providers/TeamSongsProvider';
import { Formik } from 'formik';

import { OnboardingContext } from './Onboarding';
import styles from './Onboarding.module.scss';

type GenerateSetlistFormSchema = {
	setlistSource: string;
	preSermonSongs: number;
	postSermonSongs: number;
	biblePassage: string;
};

const SETLIST_SOURCE_ITEMS: SelectOptionsListItem[] = [
	{
		title: 'Asaph Top 20',
		subtitle: 'Top rated across our network',
		value: 'asaphTop10',
	},
	{
		title: 'CCLI Top 20',
		subtitle: 'Industry chart toppers',
		value: 'ccliTop10',
	},
];

const SONG_COUNT_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({
	label: String(n),
	value: n,
}));

const StepSix = () => {
	const { team, hasMilestones } = useTeam();
	const { teamSongs, refetch } = useTeamSongs();
	const { handleError } = useErrorHandler();
	const { setStep, step, shouldAddService, setGenerateSettings, completedStepIndices } =
		React.useContext(OnboardingContext);
	const fireSelectSource = useAnalyticsEvent('Onboarding_Step6_Select_Source');
	const fireGenerateSetlist = useAnalyticsEvent('Onboarding_Step6_Generate_Setlist');

	const hasSongs = hasMilestones([TeamMilestones.ADDED_INITIAL_SONGS]);

	const initialValues: GenerateSetlistFormSchema = useMemo(
		() => ({
			setlistSource: SETLIST_SOURCE_ITEMS[0].value as string,
			preSermonSongs: 2,
			postSermonSongs: 2,
			biblePassage: '',
		}),
		[],
	);
	const { mutateAsync: getSongCollectionMutation } =
		useGetSongColletionMutation();
	const { mutateAsync: addSongsToMyTeam, isLoading: submitting } =
		useAddSongsToMyTeamMutation();
	const { mutateAsync: generateSetlistMutation } =
		useGenerateSetlistMutation();

	return (
		<Formik
			initialValues={initialValues}
			enableReinitialize
			onSubmit={async (values) => {
				try {
					fireGenerateSetlist({
						source: values.setlistSource,
						preSermonSongs: values.preSermonSongs,
						postSermonSongs: values.postSermonSongs,
						hasBiblePassage: !!values.biblePassage,
					});
					const data = await getSongCollectionMutation({
						collectionId: values.setlistSource,
					});
					const songIds = data.getSongCollection.songIds.filter(
						(id) => !teamSongs.find((ts) => ts.originSongId === id),
					);

					await addSongsToMyTeam({ songIds });
					refetch();
					const postSermonSongPosition =
						values.postSermonSongs > 0
							? values.preSermonSongs + 1
							: null;
					const generatedSetlist = await generateSetlistMutation({
						teamId: team.id,
						request: {
							positions: generatePositions({
								totalPositionsNumber:
									values.preSermonSongs +
									values.postSermonSongs,
								postSermonSongPosition,
								bibleReference: values.biblePassage,
							}),
						},
					});
					setGenerateSettings({
						totalSongPositions:
							values.preSermonSongs + values.postSermonSongs,
						postSermonSongPosition,
						bibleReference: values.biblePassage,
						initialSongIds:
							generatedSetlist.generateSetlist.positions.map(
								(position) => position!.id,
							),
					});
					setStep(step + 1);
				} catch (error) {
					handleError({
						errorCode: 'GenerateSetlistError',
						error,
						friendlyMessage: `There was an issue with generating the setlist`,
					});
				}
			}}
		>
			{({ handleSubmit, handleChange, setFieldValue, values }) => (
				<BottomActionLayout
					className={classNames([styles['layout']])}
					listClassName={styles['step-six']}
					contentClassName={styles['form']}
					topContent={
						<IndexIndicator
							className={styles['indicator']}
							dotClassName={styles['dot']}
							total={6}
							current={4}
							variant="stretched"
							onIndicatorClick={(i) => setStep(i + 2)}
							hasHover
							conditionalItems={[
								{ index: 3, visible: shouldAddService },
							]}
							disabledItems={[{ index: 5, enabled: false }]}
							hasDataItems={completedStepIndices}
						/>
					}
					actionContent={
						<>
							<AsaphButton
								color="grey"
								label="Back"
								onClick={() =>
									setStep(step - (shouldAddService ? 1 : 2))
								}
							/>
							<RegenenerateButton
								onClick={() => handleSubmit()}
								className={styles['regenerate-button']}
								isLoading={submitting}
							/>
						</>
					}
				>
					<div className={styles['title-header']}>
						<div className={styles['heading']}>
							Generate a setlist
						</div>
						<div className={classNames([styles['text'], 'mt-sm'])}>
							{hasSongs
								? `Starting with ${teamSongs.length} songs. Add more later.`
								: 'Start with 20 songs. Add yours later.'}
						</div>
					</div>

					{!hasSongs && (
						<>
							<SelectOptionsList
								items={SETLIST_SOURCE_ITEMS}
								value={values.setlistSource}
								onChange={(value) => {
									setFieldValue('setlistSource', value);
									fireSelectSource({ source: value as string });
								}}
							/>
							<div className="p-sm" />
						</>
					)}

					<div className={styles['song-counts-row']}>
						<div className={styles['song-count-field']}>
							<Label>Pre-sermon songs</Label>
							<Select
								placeholder="Select"
								items={SONG_COUNT_OPTIONS}
								onChange={(value) => {
									if (typeof value === 'number') {
										setFieldValue('preSermonSongs', value);
									}
								}}
								value={values.preSermonSongs}
							/>
						</div>
						<div className={styles['song-count-field']}>
							<Label>Post-sermon songs</Label>
							<Select
								placeholder="Select"
								items={SONG_COUNT_OPTIONS}
								onChange={(value) => {
									if (typeof value === 'number') {
										setFieldValue('postSermonSongs', value);
									}
								}}
								value={values.postSermonSongs}
							/>
						</div>
					</div>

					<div className="p-sm" />

					<Label
						tooltipHeading="Bible passage"
						tooltip="Match your post-sermon song to the primary passage planned for the sermon. Enter the passage using standard bible citation **Book chapter:verses** (e.g. John 3:16-21)."
					>
						Bible passage
					</Label>
					<BiblePassageInput
						placeholder="John 14:1-14"
						name="biblePassage"
						id="biblePassage"
						value={values.biblePassage}
						onChange={(value) => setFieldValue('biblePassage', value)}
					/>
				</BottomActionLayout>
			)}
		</Formik>
	);
};

export default StepSix;
